import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';

// Duotone investment SVG icons — gold primary on dark navy background
const ProfitIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="20" cy="27" rx="12" ry="10" fill="#D4A520"/>
    <path d="M14 17c0 0 1-5 6-5s6 5 6 5" stroke="#D4A520" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <path d="M14 19c2-2 10-2 12 0" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none"/>
    <text x="20" y="31" textAnchor="middle" fontSize="12" fill="#0B1F3B" fontWeight="bold">$</text>
    <path d="M30 10l5-5" stroke="#D4A520" strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M33 5l2 0 0 2" stroke="#D4A520" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const StockMarketIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="6" width="36" height="24" rx="3" fill="#D4A520"/>
    <rect x="2" y="32" width="36" height="4" rx="2" fill="#D4A520"/>
    <path d="M7 26L12 18L17 22L22 14L27 18L35 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="35" cy="9" r="2.5" fill="white"/>
  </svg>
);

const OnlineTradingIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="36" height="24" rx="3" fill="#D4A520"/>
    <rect x="13" y="28" width="14" height="3" rx="1" fill="#D4A520"/>
    <rect x="9" y="31" width="22" height="4" rx="2" fill="#D4A520"/>
    <path d="M7 23L12 16L17 20L22 12L27 16L35 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="35" cy="8" r="2.5" fill="white"/>
  </svg>
);

const BENEFIT_ICONS = {
  profit: ProfitIcon,
  stockmarket: StockMarketIcon,
  onlinetrading: OnlineTradingIcon,
};

const BENEFITS = [
  {
    icon: 'profit',
    label: 'Weekly Deal Flow',
    description: 'Curated investment opportunities every Monday',
  },
  {
    icon: 'stockmarket',
    label: 'Market Insights',
    description: 'AI-powered analysis tailored for women investors',
  },
  {
    icon: 'onlinetrading',
    label: 'Free Course Access',
    description: 'Preview WIN Academy lessons before you join',
  },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WarningIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="9" x2="12" y2="13" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
    <line x1="12" y1="17" x2="12.01" y2="17" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const EmailCaptureSection = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error | duplicate | invalid | valid
  const [errorMsg, setErrorMsg] = useState('');
  const [shake, setShake] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const validateEmail = (val) => {
    if (!val?.trim()) return 'empty';
    if (!EMAIL_REGEX?.test(val?.trim())) return 'invalid';
    return 'valid';
  };

  const handleChange = (e) => {
    const val = e?.target?.value;
    setEmail(val);
    // Real-time validation after first blur
    if (hasBlurred) {
      const result = validateEmail(val);
      if (result === 'valid') {
        setStatus('valid');
        setErrorMsg('');
      } else if (result === 'invalid') {
        setStatus('invalid');
        setErrorMsg('Please enter a valid email address (e.g. name@example.com)');
      } else {
        setStatus('idle');
        setErrorMsg('');
      }
    } else {
      // Clear error when typing before first blur
      if (status === 'invalid' || status === 'error') {
        setStatus('idle');
        setErrorMsg('');
      }
    }
  };

  const handleBlur = () => {
    setHasBlurred(true);
    if (!email?.trim()) {
      setStatus('idle');
      setErrorMsg('');
      return;
    }
    const result = validateEmail(email);
    if (result === 'valid') {
      setStatus('valid');
      setErrorMsg('');
    } else {
      setStatus('invalid');
      setErrorMsg('Please enter a valid email address (e.g. name@example.com)');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setHasBlurred(true);
    if (!email?.trim()) {
      setStatus('invalid');
      setErrorMsg('Please enter your email address');
      triggerShake();
      return;
    }
    if (!EMAIL_REGEX?.test(email?.trim())) {
      setStatus('invalid');
      setErrorMsg('Please enter a valid email address (e.g. name@example.com)');
      triggerShake();
      return;
    }
    setStatus('loading');
    try {
      const { error } = await supabase?.from('email_subscribers')?.insert([
        {
          email: email?.trim()?.toLowerCase(),
          source: 'landing_page',
          is_active: true,
        },
      ]);

      if (error) {
        if (error?.code === '23505') {
          setStatus('duplicate');
        } else {
          setStatus('error');
          setErrorMsg(error?.message || 'Something went wrong. Please try again.');
        }
        return;
      }
      setSubmittedEmail(email?.trim()?.toLowerCase());
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  const getBorderStyle = () => {
    if (status === 'invalid' || status === 'error') return '2px solid #EF4444';
    if (status === 'valid') return '2px solid #22C55E';
    return '2px solid transparent';
  };

  return (
    <section
      id="email-capture"
      className="relative pt-0 pb-8 sm:pb-14 overflow-hidden"
      style={{ background: '#0B1F3B' }}
    >
      {/* Gold geometric pattern overlay */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="email-geo" width="80" height="80" patternUnits="userSpaceOnUse">
              <polygon points="40,5 75,25 75,55 40,75 5,55 5,25" fill="none" stroke="#D4A520" strokeWidth="1" />
              <line x1="40" y1="5" x2="40" y2="75" stroke="#D4A520" strokeWidth="0.5" />
              <line x1="5" y1="25" x2="75" y2="55" stroke="#D4A520" strokeWidth="0.5" />
              <line x1="75" y1="25" x2="5" y2="55" stroke="#D4A520" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#email-geo)" />
        </svg>
        {/* Radial glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-[#D4A520]/5 blur-3xl" />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6">
        {/* Headline */}
        <div className="text-center mb-6 sm:mb-10">
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: '#D4A520' }}
          >
            Get Exclusive Investment Insights — Free
          </h2>
          <p className="text-white text-sm sm:text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Join 5,000+ women investors. Get weekly deal flow, market tips, and WIN Academy previews delivered to your inbox.
          </p>
        </div>

        {/* Benefit Highlights */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 mb-6 sm:mb-10">
          {BENEFITS?.map((benefit) => {
            const IconComp = BENEFIT_ICONS?.[benefit?.icon];
            return (
              <div
                key={benefit?.label}
                className="flex flex-col items-center text-center px-4 py-5 sm:py-6 rounded-xl border border-[#D4A520]/20 bg-white/5 hover:bg-white/10 hover:border-[#D4A520]/40 transition-all duration-300"
              >
                <div className="mb-0">{IconComp && <IconComp />}</div>
                <span
                  className="font-bold text-base mt-3 mb-1"
                  style={{ color: '#D4A520', fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {benefit?.label}
                </span>
                <span className="text-white text-sm leading-relaxed">{benefit?.description}</span>
              </div>
            );
          })}
        </div>

        {/* Form or Success State */}
        {status === 'success' ? (
          <div
            className="flex flex-col items-center gap-4 py-8"
            style={{ animation: 'fadeInUp 0.4s ease forwards' }}
          >
            {/* Large animated gold checkmark */}
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(212,165,32,0.12)', border: '2.5px solid #D4A520' }}
            >
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <path
                  d="M8 22l10 10 18-18"
                  stroke="#D4A520"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    animation: 'drawCheck 0.55s ease 0.1s forwards',
                    strokeDasharray: 52,
                    strokeDashoffset: 52,
                  }}
                />
              </svg>
            </div>
            <h3
              className="text-2xl sm:text-3xl font-bold text-center"
              style={{ color: '#D4A520', fontFamily: 'Cormorant Garamond, serif' }}
            >
              You're in!
            </h3>
            <p className="text-white text-center text-sm sm:text-base max-w-sm">
              Check your inbox for a welcome gift. We sent it to{' '}
              <span className="font-bold text-white">{submittedEmail}</span>
            </p>
            <a
              href="/?ref=share"
              className="text-sm font-semibold transition-opacity hover:opacity-80"
              style={{ color: '#D4A520' }}
            >
              Share WIN with a friend →
            </a>
          </div>
        ) : status === 'duplicate' ? (
          <div className="flex flex-col items-center gap-3 py-6" style={{ animation: 'fadeInUp 0.4s ease forwards' }}>
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(212,165,32,0.15)', border: '2px solid #D4A520' }}
            >
              <span className="text-xl">✉️</span>
            </div>
            <p className="font-semibold text-center text-sm sm:text-base" style={{ color: '#D4A520' }}>
              You're already on our list! We'll see you in your inbox.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className={`flex flex-col sm:flex-row gap-3 mb-1 ${shake ? 'animate-shake' : ''}`}>
              <div className="flex-1 relative">
                <input
                  type="email"
                  value={email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter your email address"
                  className="w-full px-5 pr-12 h-12 rounded-xl bg-white text-[#0B1F3B] placeholder-gray-400 text-base outline-none transition-all duration-300"
                  style={{
                    border: getBorderStyle(),
                    boxShadow: status === 'valid' ? '0 0 0 3px rgba(34,197,94,0.15)' : status === 'invalid' || status === 'error' ? '0 0 0 3px rgba(239,68,68,0.15)' : 'none',
                  }}
                  onFocus={(e) => {
                    if (status !== 'invalid' && status !== 'error' && status !== 'valid') {
                      e.target.style.border = '2px solid #D4A520';
                      e.target.style.boxShadow = '0 0 0 3px rgba(212,165,32,0.2)';
                    }
                  }}
                  onBlurCapture={(e) => {
                    if (status !== 'invalid' && status !== 'error' && status !== 'valid') {
                      e.target.style.border = '2px solid transparent';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                  disabled={status === 'loading'}
                />
                {/* Valid checkmark icon */}
                {status === 'valid' && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ animation: 'fadeInIcon 0.2s ease forwards' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#22C55E" />
                      <path d="M7 12l4 4 6-7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
                {/* Invalid X icon */}
                {(status === 'invalid' || status === 'error') && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#EF4444" />
                      <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-8 h-12 rounded-xl font-bold text-base transition-all duration-300 whitespace-nowrap"
                style={{
                  background: '#D4A520',
                  color: '#0B1F3B',
                  opacity: status === 'loading' ? 0.7 : 1,
                }}
                onMouseEnter={(e) => { if (status !== 'loading') e.currentTarget.style.transform = 'scale(1.03)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {status === 'loading' ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="#0B1F3B" strokeWidth="3" strokeDasharray="31.4" strokeDashoffset="10" />
                    </svg>
                    Subscribing...
                  </span>
                ) : 'Claim Your Spot'}
              </button>
            </div>

            {/* Error message */}
            {(status === 'invalid' || status === 'error') && errorMsg && (
              <div
                className="flex items-center gap-1.5 mt-1 mb-2"
                style={{ animation: 'fadeInError 0.2s ease forwards' }}
              >
                <WarningIcon />
                <p className="text-sm" style={{ color: '#EF4444' }}>{errorMsg}</p>
              </div>
            )}

            {/* Trust line */}
            <div className={`flex flex-col items-center gap-2 ${(status === 'invalid' || status === 'error') && errorMsg ? 'mt-2' : 'mt-4'}`}>
              <div className="flex items-center gap-2 text-white text-xs">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="11" width="18" height="11" rx="2" stroke="#D4A520" strokeWidth="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" stroke="#D4A520" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>No spam. Unsubscribe anytime. Your data is safe with WIN.</span>
              </div>
              <p className="text-white/60 text-xs">
                By subscribing you agree to our{' '}
                <span className="text-[#D4A520]/60 underline cursor-pointer hover:text-[#D4A520] transition-colors">Privacy Policy</span>
              </p>
            </div>
          </form>
        )}
      </div>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes drawCheck {
          to { stroke-dashoffset: 0; }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-8px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
          90% { transform: translateX(3px); }
        }
        .animate-shake {
          animation: shake 0.6s ease;
        }
        @keyframes fadeInError {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInIcon {
          from { opacity: 0; transform: translateY(-50%) scale(0.7); }
          to { opacity: 1; transform: translateY(-50%) scale(1); }
        }
      `}</style>
    </section>
  );
};

export default EmailCaptureSection;
